import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';

const Base = chakra('h2', {
  base: {
    fontFamily: 'heading',
    color: 'text.primary',
    margin: 0,
    textWrap: 'balance',
    // Never let a large display heading clip its container: allow the flex/grid child to
    // shrink (min-width:0), cap at the content box, and break the odd over-long token.
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'break-word',
  },
});

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
type HeadingSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'display';

/** Confident, tight-tracked editorial scale (Bricolage Grotesque). */
const sizeStyles: Record<HeadingSize, HTMLChakraProps<'h2'>> = {
  sm: { fontSize: 'lg', fontWeight: 'semibold', lineHeight: 'snug', letterSpacing: 'tight' },
  md: { fontSize: 'xl', fontWeight: 'semibold', lineHeight: 'snug', letterSpacing: 'tight' },
  lg: { fontSize: '2xl', fontWeight: 'bold', lineHeight: 'tight', letterSpacing: 'tight' },
  // The large display sizes are fluid: they scale down in narrow containers (e.g. the
  // split light/dark gallery columns) so they never overflow, and reach their token max on
  // wide viewports. Caps equal the former fixed sizes (3xl/4xl/5xl/6xl = 28/36/46/60px).
  xl: {
    fontSize: 'clamp(1.5rem, 1.16rem + 1.7vw, 1.75rem)',
    fontWeight: 'bold',
    lineHeight: 'tight',
    letterSpacing: 'tight',
  },
  '2xl': {
    fontSize: 'clamp(1.625rem, 1.14rem + 2.4vw, 2.25rem)',
    fontWeight: 'extrabold',
    lineHeight: 'tight',
    letterSpacing: 'tighter',
  },
  '3xl': {
    fontSize: 'clamp(1.875rem, 1.3rem + 2.9vw, 2.875rem)',
    fontWeight: 'extrabold',
    lineHeight: 'tight',
    letterSpacing: 'tighter',
  },
  display: {
    fontSize: 'clamp(2.25rem, 1.4rem + 4.25vw, 3.75rem)',
    fontWeight: 'extrabold',
    lineHeight: 'none',
    letterSpacing: 'tighter',
  },
};

const defaultSizeForLevel: Record<HeadingLevel, HeadingSize> = {
  1: '2xl',
  2: 'xl',
  3: 'lg',
  4: 'md',
  5: 'sm',
  6: 'sm',
};

export interface HeadingProps extends Omit<React.HTMLAttributes<HTMLHeadingElement>, 'color'> {
  /** Semantic heading level (h1–h6). Drives the tag and the default size. */
  level?: HeadingLevel;
  /** Visual size, decoupled from the semantic level when needed. */
  size?: HeadingSize;
  tone?: 'primary' | 'inverse' | 'brand';
  children?: ReactNode;
}

const toneColor = {
  primary: 'text.primary',
  inverse: 'text.inverse',
  brand: 'brand.fg',
} as const;

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(function Heading(
  { level = 2, size, tone = 'primary', ...rest },
  ref,
) {
  const resolved = size ?? defaultSizeForLevel[level];
  return (
    <Base as={`h${level}`} ref={ref} color={toneColor[tone]} {...sizeStyles[resolved]} {...rest} />
  );
});
